/**
 * Multi-Stage Completion Provider
 * Handles progressive completion: command → argument → environment
 * 
 * Example flow:
 * 1. User types `\` → shows commands including `\begin`
 * 2. User selects `\begin` → inserts `\begin{}` with cursor in braces
 * 3. User types in braces → shows environment suggestions
 * 4. User selects environment → inserts full `\begin{env}...\end{env}`
 */

import { CompletionContext, CompletionResult } from '@codemirror/autocomplete'
import { EditorView } from '@codemirror/view'
import { PositionInfo, LaTeXContext } from './types'
// Command registry imports not needed here
import { findEnvironments } from '@/utils/latexDataLoader'
import { parseContext } from './contextParser'
import { analyzePosition } from './positionAnalyzer'
// Snippet engine imports not needed here - handled in main provider
import { logDebug } from '@utils/logger'

/**
 * Main multi-stage completion provider
 */
export function multiStageCompletion(context: CompletionContext): CompletionResult | null {
  const { state, pos } = context
  const text = state.doc.toString()
  
  const position = analyzePosition(text, pos)
  const latexContext = parseContext(text, pos)
  
  // Stage 1: Check if we're in \begin{...} - show environment suggestions
  if (position.isInEnvironment && position.environmentName !== undefined) {
    return provideEnvironmentCompletions(context, position, latexContext)
  }
  
  // Stage 2: Check if we're after \begin (no brace yet) - insert \begin{} and trigger environment completion
  if (position.isInCommand && position.commandName === 'begin') {
    return provideBeginCommandCompletion(context, position)
  }
  
  // Stage 3: Normal command completion (handled by command provider)
  // This will be integrated with the main provider system
  
  return null
}

/**
 * Provide environment completions when inside \begin{...}
 */
function provideEnvironmentCompletions(
  context: CompletionContext,
  position: PositionInfo,
  latexContext: LaTeXContext
): CompletionResult | null {
  const { state, pos } = context
  const text = state.doc.toString()
  
  // Find the \begin{ position
  let beginPos = -1
  let envStart = pos
  
  // Scan backwards to find \begin{
  for (let i = pos - 1; i >= 0 && (pos - i) <= 100; i--) {
    if (text.substring(i - 6, i + 1) === '\\begin{') {
      beginPos = i - 6
      envStart = i + 1
      break
    }
  }
  
  if (beginPos < 0) return null
  
  // Get current query (text after {)
  const query = text.substring(envStart, pos).toLowerCase()
  
  // Get environment matches
  const envMatches = findEnvironments(query)
  
  if (envMatches.length === 0) return null
  
  const capturedBeginPos = beginPos
  const capturedPos = pos
  
  const options = envMatches.slice(0, 30).map(envName => {
    const beginTag = `\\begin{${envName}}`
    const endTag = `\\end{${envName}}`
    
    // Smart insertion: put cursor inside environment with proper indentation
    const insertText = `${beginTag}\n\t$0\n${endTag}`
    const cursorPos = capturedBeginPos + beginTag.length + 2 // After \n\t
    
    return {
      label: envName,
      type: 'keyword' as const,
      info: `LaTeX environment: ${envName}`,
      apply: (view: EditorView) => {
        // Replace from \begin{ to current position
        view.dispatch({
          changes: {
            from: capturedBeginPos,
            to: capturedPos,
            insert: insertText
          },
          selection: {
            anchor: cursorPos
          }
        })
      }
    }
  })
  
  return {
    from: envStart,
    to: pos,
    options,
    filter: false,
    span: /^[a-zA-Z*]*$/, // Valid characters for environment names
    validFor: /^[a-zA-Z*]*$/
  } as CompletionResult & { span: RegExp; validFor: RegExp }
}

/**
 * Provide completion for \begin command
 * Inserts \begin{} with cursor in braces to trigger environment completion
 */
function provideBeginCommandCompletion(
  context: CompletionContext,
  position: PositionInfo
): CompletionResult | null {
  const { state, pos } = context
  const text = state.doc.toString()
  
  // Find backslash position
  let backslashPos = -1
  for (let i = pos - 1; i >= 0 && (pos - i) <= 50; i--) {
    if (text[i] === '\\' && (i === 0 || text[i - 1] !== '\\')) {
      backslashPos = i
      break
    }
    if (text[i] === ' ' || text[i] === '\n' || text[i] === '\t') {
      break
    }
  }
  
  if (backslashPos < 0) return null
  
  // Check if we already have \begin{ (user might be typing)
  const afterBackslash = text.substring(backslashPos + 1)
  if (afterBackslash.startsWith('begin{')) {
    // Already have brace, let environment provider handle it
    return null
  }
  
  // Provide option to insert \begin{}
  const insertText = 'begin{$0}'
  const cursorPos = backslashPos + 1 + insertText.indexOf('$0')
  
  return {
    from: backslashPos + 1,
    to: pos,
    options: [{
      label: 'begin',
      type: 'function' as const,
      info: 'Begin environment - type environment name in braces',
      apply: (view: EditorView) => {
        // Insert \begin{} with cursor inside braces
        const fullText = `\\begin{}`
        const cursorPosition = backslashPos + 7 // Position after {
        
        view.dispatch({
          changes: {
            from: backslashPos,
            to: pos,
            insert: fullText
          },
          selection: {
            anchor: cursorPosition
          }
        })
        
        // Trigger autocomplete for environments after a short delay
        // This will be handled by the environment provider
      }
    }],
    filter: false,
    span: /^begin$/,
    validFor: /^begin$/
  } as CompletionResult & { span: RegExp; validFor: RegExp }
}

